import classNames from 'classnames';
import moment from 'moment-timezone';
import React, { memo, useCallback, useState } from 'react';

import ActivityChip from 'common/ActivityChip';
import ExtraActivityTimelineManagement from 'common/ExtraActivityTimelineManagement';
import { useExtraActivityHoverRetention } from 'common/ExtraActivityTimelineManagement/useExtraActivityHoverRetention';
import ShiftTrack from 'common/ShiftTrack';
import { formatTime } from 'utils/datetime';

import { TIMELINE_ROW_TEXT } from './text';

import styles from './styles.module.css';

const MIN_HOVER_WIDTH_PX = 184;
const MIN_EXTRA_HOVER_WIDTH_PX = 80;
const ACTIVE_ITEM_TOP_PX = 32;
const SHIFT_TAIL_MAX_FLEX_MINUTES = 60;
const SHIFT_TAIL_MAIN_ACTIVITY_GROW_RATIO = 0.25;
const MAX_SHIFT_DENSITY_CHILD_COUNT = 12;

const getActivityId = (record) => record?.id ?? record?.key ?? null;

const getActivityShiftId = (record) =>
	record?.shiftId ?? record?.activity?.shiftId ?? record?.activity?.shift?.id ?? null;

const buildActiveItemPayload = (record, driverId, position, widthPx) => ({
	...record,
	id: getActivityId(record),
	shiftId: getActivityShiftId(record),
	crewMemberId: driverId,
	leftPercent: position?.leftPercent ?? 0,
	widthPercent: position?.widthPercent ?? 0,
	widthPx: widthPx ?? 0,
});

const LABELED_HOURS = [0, 4, 8, 12, 16, 20, 24];

const renderHourLines = () =>
	LABELED_HOURS.map((hour) => {
		if (hour === 0) {
			return null;
		}

		const position = (hour / 24) * 100;

		return (
			<div
				key={`hour-line-${hour}`}
				className={styles.hourLine}
				style={{ left: `calc(${position}% - 0.5px)` }}
			/>
		);
	});

const renderDayColumns = (visibleDateRange) =>
	visibleDateRange.map((date) => (
		<div key={date.date} className={styles.dayColumn}>
			{renderHourLines()}
		</div>
	));

const renderShiftTracks = (shifts, visibleDateRange, timezone, getCachedPosition) =>
	shifts?.map((shift) => {
		const position = getCachedPosition(shift, visibleDateRange, timezone);

		if (!position) {
			return null;
		}

		const { leftPercent, widthPercent } = position;

		return (
			<div
				key={`track-${shift.id}`}
				className={styles.shift}
				style={{
					left: `${leftPercent}%`,
					width: `${widthPercent}%`,
				}}
			>
				<ShiftTrack timezone={timezone} shift={shift} />
			</div>
		);
	});

const getChildKindClass = (record) => {
	if (record.isExtraActivity) return styles.shiftChildExtra;
	if (record.isShiftTail) return styles.shiftChildTail;
	return styles.shiftChildBase;
};

const getChildClassName = (record, isExtraActivityManagementActive = false) => {
	return classNames(getChildKindClass(record), {
		[styles.shiftChildHoverable]: !record.isShiftTail,
		[styles.shiftChildExtraActivityActive]: isExtraActivityManagementActive,
	});
};

const getShiftDensityChildCount = (children = []) =>
	Math.min(children.length, MAX_SHIFT_DENSITY_CHILD_COUNT);

const formatAutoCloseTime = (timestamp, timezone) => {
	if (!timestamp) return null;

	const value = moment(timestamp).tz(timezone || moment.tz.guess());

	return value.isValid() ? formatTime(timestamp, { timezone }) : '--:--';
};

const getDurationMinutes = (record) => {
	const start = moment(record.fromTimestamp);
	const end = moment(record.toTimestamp);
	const durationMinutes = end.diff(start, 'minutes');

	if (!start.isValid() || !end.isValid() || durationMinutes <= 0) return null;

	return durationMinutes;
};

const getShortestMainActivityDuration = (shiftChildren = []) => {
	const mainDurations = shiftChildren
		.filter((record) => !record.isExtraActivity && !record.isShiftTail)
		.map(getDurationMinutes)
		.filter(Boolean);

	return mainDurations.length ? Math.min(...mainDurations) : null;
};

const getChildFlexStyle = (record, shiftChildren = []) => {
	if (record.isExtraActivity) return undefined;

	const durationMinutes = getDurationMinutes(record);

	if (!durationMinutes) return undefined;

	if (record.isShiftTail) {
		const shortestMainDuration = getShortestMainActivityDuration(shiftChildren);
		const tailMaxFlexMinutes = Math.min(
			SHIFT_TAIL_MAX_FLEX_MINUTES,
			shortestMainDuration
				? shortestMainDuration * SHIFT_TAIL_MAIN_ACTIVITY_GROW_RATIO
				: SHIFT_TAIL_MAX_FLEX_MINUTES
		);

		return { flexGrow: Math.min(durationMinutes, tailMaxFlexMinutes) };
	}

	return { flexGrow: durationMinutes };
};

const renderShiftChild = ({
	childRecord,
	shiftChildren,
	driverId,
	timezone,
	extraActivityActiveItemId,
	onExtraActivityManagementInteractionChange,
	onShiftChildEnter,
}) => {
	const childKey = `${driverId}-${childRecord.key}`;
	const chipRecord = { ...childRecord, crewMemberId: driverId };
	const childActivityId = getActivityId(childRecord);

	const autoCloseTs = childRecord.isShiftTail ? childRecord.activity?.autoCloseTimestamp : null;
	const autoCloseLabel = formatAutoCloseTime(autoCloseTs, timezone);

	return (
		<div
			key={childKey}
			data-testid={`shift-child-${childRecord.key}`}
			className={getChildClassName(childRecord, extraActivityActiveItemId === childActivityId)}
			style={getChildFlexStyle(childRecord, shiftChildren)}
			onMouseEnter={childRecord.isShiftTail ? undefined : onShiftChildEnter}
		>
			<div className={styles.shiftChildContent}>
				<ActivityChip
					timezone={timezone}
					record={chipRecord}
					extraActivityManagement={<ExtraActivityTimelineManagement record={chipRecord} />}
					onExtraActivityManagementInteractionChange={(isActive) =>
						onExtraActivityManagementInteractionChange?.(childActivityId, isActive)
					}
					suppressNativeTitle
				/>
			</div>
			{autoCloseTs && (
				<>
					<div className={styles.autoCloseLine} />
					<div className={styles.autoCloseTimeLabel}>{autoCloseLabel}</div>
					<div className={styles.autoCloseTextLabel}>{TIMELINE_ROW_TEXT.autoClose}</div>
				</>
			)}
		</div>
	);
};

const renderShiftGroup = ({
	groupRecord,
	visibleDateRange,
	timezone,
	driverId,
	extraActivityActiveItemId,
	onExtraActivityManagementInteractionChange,
	onShiftChildEnter,
	getCachedPosition,
}) => {
	const position = getCachedPosition(groupRecord, visibleDateRange, timezone);

	if (!position) return null;

	const { leftPercent, widthPercent } = position;

	return (
		<div
			key={`${driverId}-${groupRecord.shift.id}`}
			data-testid={`shift-group-${groupRecord.shift.id}`}
			data-shift-density-count={getShiftDensityChildCount(groupRecord.children)}
			className={classNames(styles.shiftGroup, {
				[styles.pseudoShiftGroup]: groupRecord.isPseudoShiftGroup,
			})}
			style={{
				left: `${leftPercent}%`,
				width: `${widthPercent}%`,
			}}
		>
			{groupRecord.children.map((child) =>
				renderShiftChild({
					childRecord: child,
					shiftChildren: groupRecord.children,
					driverId,
					timezone,
					extraActivityActiveItemId,
					onExtraActivityManagementInteractionChange,
					onShiftChildEnter,
				})
			)}
		</div>
	);
};

const renderOrphanActivity = ({
	record,
	visibleDateRange,
	timezone,
	driverId,
	activeItemId,
	extraActivityActiveItemId,
	onSetActiveItem,
	onExtraActivityManagementInteractionChange,
	getCachedPosition,
}) => {
	const position = getCachedPosition(record, visibleDateRange, timezone);

	if (!position) return null;

	const { leftPercent, widthPercent } = position;
	const activityId = getActivityId(record);
	const isActiveItem = activeItemId === activityId;
	const chipRecord = { ...record, crewMemberId: driverId };

	return (
		<div
			key={`${driverId}-${record.key}`}
			className={classNames(styles.activityChip, {
				[styles.activityChipHidden]: isActiveItem,
				[styles.activityChipExtraActivityActive]: extraActivityActiveItemId === activityId,
			})}
			style={{
				left: `${leftPercent}%`,
				width: `${widthPercent}%`,
			}}
			onMouseEnter={(event) => {
				if (!record.isHoverable) return;

				const measuredWidthPx = event.currentTarget.getBoundingClientRect().width;
				onSetActiveItem(buildActiveItemPayload(record, driverId, position, measuredWidthPx));
			}}
		>
			<ActivityChip
				timezone={timezone}
				record={chipRecord}
				extraActivityManagement={<ExtraActivityTimelineManagement record={chipRecord} />}
				onExtraActivityManagementInteractionChange={(isActive) =>
					onExtraActivityManagementInteractionChange(activityId, isActive)
				}
			/>
		</div>
	);
};

const TimelineRow = ({
	driver,
	visibleDateRange,
	timezone,
	daysCount,
	rowActiveItem,
	rowActiveItemId,
	onSetActiveItem,
	onClearActiveItem,
	getCachedPosition,
}) => {
	const [extraActivityActiveItemId, setExtraActivityActiveItemId] = useState(null);
	const activityHoverLength = rowActiveItem?.isExtraActivity
		? MIN_EXTRA_HOVER_WIDTH_PX
		: MIN_HOVER_WIDTH_PX;

	const activeCloneWidthPx = rowActiveItem
		? Math.max(activityHoverLength, rowActiveItem.widthPx || 0)
		: 0;
	const { handleExtraActivityInteractionChange, requestClearActiveItem, resetPendingClear } =
		useExtraActivityHoverRetention({
			activeItemId: rowActiveItemId,
			onClearActiveItem,
		});
	const handleExtraActivityManagementInteractionChange = useCallback((activityId, isActive) => {
		setExtraActivityActiveItemId((currentActivityId) => {
			if (isActive) return activityId;
			if (currentActivityId === activityId) return null;

			return currentActivityId;
		});
	}, []);

	const activities = driver.activities ?? [];

	return (
		<div className={styles.activityRow}>
			<div
				data-testid="timeline-container"
				className={styles.timelineContainer}
				style={{ '--days-count': daysCount }}
			>
				{renderDayColumns(visibleDateRange)}

				{renderShiftTracks(driver.shifts, visibleDateRange, timezone, getCachedPosition)}

				{activities.map((record) => {
					if (record.isShiftGroup) {
						return renderShiftGroup({
							groupRecord: record,
							visibleDateRange,
							timezone,
							driverId: driver.id,
							extraActivityActiveItemId,
							onExtraActivityManagementInteractionChange:
								handleExtraActivityManagementInteractionChange,
							onShiftChildEnter: onClearActiveItem,
							getCachedPosition,
						});
					}

					return renderOrphanActivity({
						record,
						visibleDateRange,
						timezone,
						driverId: driver.id,
						activeItemId: rowActiveItemId,
						extraActivityActiveItemId,
						onSetActiveItem,
						onExtraActivityManagementInteractionChange:
							handleExtraActivityManagementInteractionChange,
						getCachedPosition,
					});
				})}

				{rowActiveItem && (
					<div
						data-testid="row-active-item-hover-zone"
						className={styles.activeItemHoverZone}
						style={{
							left: `${rowActiveItem.leftPercent}%`,
							top: `${ACTIVE_ITEM_TOP_PX}px`,
							width: `${activeCloneWidthPx}px`,
						}}
						onMouseEnter={resetPendingClear}
						onMouseLeave={requestClearActiveItem}
					>
						<div
							className={styles.activeItemClone}
							style={{
								width: `${activeCloneWidthPx}px`,
							}}
						>
							<ActivityChip
								timezone={timezone}
								record={rowActiveItem}
								extraActivityManagement={<ExtraActivityTimelineManagement record={rowActiveItem} />}
								onExtraActivityManagementInteractionChange={handleExtraActivityInteractionChange}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default memo(TimelineRow);
